/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Layout from "../../../Layouts/Layout";
import { Helmet } from "react-helmet-async";
import TextContainer from "../../../../components/Container/TextContainer";
import divorcePlannerStyle from "./divorcePlannerStyle";
import DivorcePlannerNavigation from "../../../../components/DivorcePlanner/DivorcePlannerNavigation";
import DoubleDivider from "../../../../components/DoubleDivider/DoubleDivider";
import DivorcePlannerProcess from "../../../../components/DivorcePlanner/DivorcePlannerProcess";
import { STATUS_DATA_COMPLETED, STATUS_POWER_OF_ATTORNEY_GRANTED } from "../../../../services/divorce/leadStatus";
import DivorcePlannerChildren from "../../../../components/DivorcePlanner/DivorcePlannerChildren";
import { DivorcePlannerChecklists } from "../../../../components/DivorcePlanner/DivorcePlannerChecklists";
import Container from "../../../../components/Container/Container";
import LegalbirdLoader from "../../../../components/ContentLoader/LegalbirdLoader";
import _get from "lodash/get";
import { Link } from "react-router";
import { getCaseLink, getProductData } from "../../../../services/productService";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "../../../../services/reactQuery/reactQueryService";
import useProductParams from "../../../../hooks/useProductParams";
import { Box } from "@mui/material";
import { gaSet } from "../../../../services/googleAnalyticsService";

const DivorcePlannerPage = () => {
  const { productId } = useProductParams();
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId)
  });
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });

  const isAccessible = () => {
    return (
      divorce.leadStatus >= STATUS_POWER_OF_ATTORNEY_GRANTED || (_get(divorce, "caseInsurance.insurer.id") > 0 && divorce.leadStatus >= STATUS_DATA_COMPLETED)
    );
  };

  const saveChecklist = (checklist: { divorce: { [key: string]: boolean } }) => {
    updateMutation.mutate({
      uri: "/divorces",
      id: divorce.id,
      data: {
        checklist: checklist,
      },
    });
  };

  const title = "Mein Scheidungsplaner | Legalbird";
  gaSet({ title: title });
  if (isLoading) {
    return (
      <Layout backgroundType={"serviceworld"}>
        <Container>
          <Container sx={{ width: "250px" }}>
            <LegalbirdLoader />
          </Container>
        </Container>
      </Layout>
    );
  }

  const accessible = isAccessible();
  return (
    <Layout backgroundType={"serviceworld"}>
      <Box sx={divorcePlannerStyle.contentContainer}>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <TextContainer>
          <Typography variant={"h1"} sx={divorcePlannerStyle.headline}>
            Mein Scheidungsplaner
          </Typography>
          <DivorcePlannerNavigation divorce={divorce} accessible={accessible} />
          {accessible && (
            <>
              <DoubleDivider />
              <DivorcePlannerProcess divorce={divorce} id={"ablauf"} />
              <br />
              <DivorcePlannerChecklists
                divorce={divorce}
                saveChecklist={saveChecklist}
                id={"checkliste"}
                hasInsurance={_get(divorce, "caseInsurance.insurer.id") > 0}
              />
              <br />
              {divorce.numberChildren > 0 && (
                <>
                  <DivorcePlannerChildren id={"kinder"} />
                  <br />
                </>
              )}
              <Button component={Link} to={getCaseLink(divorce)} fullWidth color={"primary"} variant={"text"}>
                Zurück zum Fall
              </Button>
            </>
          )}
        </TextContainer>
      </Box>
    </Layout>
  );
};

export default DivorcePlannerPage;
